const API_URL = import.meta.env.VITE_API_URL as string;

function getAuthHeader() {
  const token = localStorage.getItem("admin_token");
  return token ? { "Authorization": `Basic ${token}` } : {};
}

async function authenticatedFetch(url: string, options: RequestInit = {}) {
  const headers = {
    ...options.headers,
    ...getAuthHeader(),
  };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem("admin_token");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  return res;
}

export interface Survey {
  id: string;
  projectId: string;
  name: string;
  slug?: string;
  status: "active" | "inactive";
  mode: "test" | "production";
  createdAt: string;
  responseCount: number;
  customJs?: string;
  labels?: {
    completeText?: string;
    completedHtml?: string;
  };
  welcomePage?: {
    title: string;
    welcomeText: string;
    expandableTitle?: string;
    expandableContent?: string;
    isExpandable?: boolean;
    moreInfoUrl?: string;
    moreInfoLabel?: string;
  };
  emailSenderName?: string;
  emailSubject?: string;
  emailBody?: string;
  emailSignature?: string;
}

export async function sendEmails(
  projectId: string,
  surveyId: string,
  payload: {
    invitations: { email: string; name: string; metadata?: Record<string, string> }[];
    surveyUrl: string;
    subject?: string;
    emailBody?: string;
    senderName?: string;
    signature?: string;
  }
): Promise<{ results: { email: string; status: "sent" | "error"; error?: string }[] }> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}/send-emails`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}


export interface ProjectBranding {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  neutralColor?: string;
  logoUrl?: string;
  fontFamily?: string;
}

export interface Project {
  id: string;
  name: string;
  status: "active" | "archived";
  createdAt: string;
  branding?: ProjectBranding;
}

export async function createProject(name: string): Promise<Project> {
  const res = await authenticatedFetch(`${API_URL}/projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listProjects(): Promise<Project[]> {
  const res = await authenticatedFetch(`${API_URL}/projects`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveyWelcomePage(
  projectId: string,
  surveyId: string,
  welcomePage: Survey["welcomePage"]
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ welcomePage }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}


export async function getProject(projectId: string): Promise<Project> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateProject(
  projectId: string,
  payload: Partial<Omit<Project, "id" | "createdAt">>
): Promise<Project> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function deleteProject(projectId: string): Promise<void> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function uploadLogo(
  projectId: string,
  file: File,
  surveyId?: string,
  logoType?: string
): Promise<{ logoUrl: string }> {
  const reader = new FileReader();
  const base64Promise = new Promise<string>((resolve) => {
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve(base64);
    };
  });
  reader.readAsDataURL(file);
  const base64 = await base64Promise;

  const url = surveyId 
    ? `${API_URL}/projects/${projectId}/surveys/${surveyId}/logo`
    : `${API_URL}/projects/${projectId}/logo`;

  const res = await authenticatedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      base64,
      contentType: file.type,
      filename: file.name,
      logoType,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function listSurveys(projectId: string): Promise<Survey[]> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createSurvey(
  projectId: string,
  payload: { name: string; slug?: string; definition: unknown; mode: "test" | "production" }
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateSurveyStatus(
  projectId: string,
  surveyId: string,
  status: "active" | "inactive"
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export interface SurveyWithDefinition extends Survey {
  definition: unknown;
}

export async function getSurvey(projectId: string, surveyId: string): Promise<SurveyWithDefinition> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveySlug(
  projectId: string,
  surveyId: string,
  slug: string
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function updateSurveyName(
  projectId: string,
  surveyId: string,
  name: string
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export async function deleteSurvey(projectId: string, surveyId: string): Promise<void> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function updateSurveyMode(
  projectId: string,
  surveyId: string,
  mode: "test" | "production"
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveyDefinition(
  projectId: string,
  surveyId: string,
  definition: unknown
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ definition }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveyCustomJs(
  projectId: string,
  surveyId: string,
  customJs: string
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customJs }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveyLabels(
  projectId: string,
  surveyId: string,
  labels: NonNullable<Survey["labels"]>
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ labels }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function updateSurveyEmailSettings(
  projectId: string,
  surveyId: string,
  settings: {
    emailSenderName: string;
    emailSubject: string;
    emailBody: string;
    emailSignature: string;
  }
): Promise<Survey> {
  const res = await authenticatedFetch(`${API_URL}/projects/${projectId}/surveys/${surveyId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
